/* Used Engines Inc. - trust controls and delivery carousel, v3.
   Keeps native product form/VIN behavior. No network calls, reviews scraping or new tracking.
   Only published delivery cards are used. Clones are presentation copies of those same cards. */
(() => {
  'use strict';
  if (!window.customElements || customElements.get('ue-product-trust')) return;
  const addLocks = new WeakMap();
  const mod = (n, m) => ((n % m) + m) % m;

  class DeliveryLoop {
    constructor(owner) {
      this.owner = owner;
      this.root = owner.root;
      this.track = owner.track;
      this.originals = Array.from(this.track.children).filter(node => node.matches('[data-ue-card]'));
      this.abort = new AbortController();
      this.signal = this.abort.signal;
      this.media = window.matchMedia('(prefers-reduced-motion: reduce)');
      this.designMode = Boolean(window.Shopify && window.Shopify.designMode);
      this.enabled = Boolean(this.root && this.root.dataset.uemAuto === 'true');
      this.delay = Math.max(3, Math.min(10, Number(this.root && this.root.dataset.uemInterval) || 5)) * 1000;
      this.paused = this.designMode;
      this.hover = false;
      this.focused = false;
      this.dragging = false;
      this.visible = !('IntersectionObserver' in window);
      this.base = 0;
      this.step = 0;
      this.cycle = 0;
      this.infinite = false;
      this.overflow = false;
      this.moving = false;
      this.controls = owner.querySelector('[data-ue-controls]');
      this.savedSnap = this.track.style.scrollSnapType;
      this.autoOption = this.root && this.root.querySelector('[data-uem-autoplay-option]');
      this.autoRow = this.root && this.root.querySelector('[data-uem-autoplay-row]');
      // Keep an explicit user preference without displaying controls in the gallery header.
      if (!this.designMode) {
        try { this.paused = sessionStorage.getItem('ue-delivery-paused') === 'true'; } catch (_) {}
      }
      if (this.autoOption) this.autoOption.addEventListener('change', () => {
        this.paused = !this.autoOption.checked;
        if (!this.designMode) {
          try { sessionStorage.setItem('ue-delivery-paused', String(this.paused)); } catch (_) {}
        }
        if (this.paused) this.cancelMove();
        this.updateControls(); this.schedule();
      }, { signal:this.signal });
      this.track.addEventListener('scroll', () => {
        this.updateControls();
        if (this.moving) return;
        clearTimeout(this.scrollTimer);
        this.scrollTimer = setTimeout(() => { this.normalize(); this.schedule(); }, 180);
      }, { passive:true, signal:this.signal });
      this.track.addEventListener('wheel', () => { this.cancelMove(); this.hold(); }, { passive:true, signal:this.signal });
      this.track.addEventListener('pointerdown', () => {
        this.dragging = true;
        this.cancelMove();
        this.hold();
      }, { passive:true, signal:this.signal });
      const release = () => {
        if (!this.dragging) return;
        this.dragging = false;
        this.normalize();
        this.schedule();
      };
      window.addEventListener('pointerup', release, { passive:true, signal:this.signal });
      window.addEventListener('pointercancel', release, { passive:true, signal:this.signal });
      owner.addEventListener('pointerenter', event => {
        if (event.pointerType === 'touch') return;
        this.hover = true; this.cancelMove(); this.hold();
      }, { signal:this.signal });
      owner.addEventListener('pointerleave', () => { this.hover = false; this.schedule(); }, { signal:this.signal });
      owner.addEventListener('focusin', event => {
        this.focused = true;
        this.cancelMove();
        this.hold();
        const clone = event.target.closest && event.target.closest('[data-ue-clone]');
        if (clone) {
          const original = this.originals[Number(clone.dataset.ueClone)];
          const linkIndex = Array.from(clone.querySelectorAll('a,button')).indexOf(event.target);
          const counterpart = original && original.querySelectorAll('a,button')[linkIndex];
          if (counterpart) {
            this.track.scrollLeft = this.base + Number(clone.dataset.ueClone) * this.step;
            counterpart.focus({ preventScroll:true });
          }
        }
      }, { signal:this.signal });
      owner.addEventListener('focusout', () => {
        clearTimeout(this.focusTimer);
        this.focusTimer = setTimeout(() => {
          this.focused = this.owner.contains(document.activeElement);
          this.normalize(); this.schedule();
        }, 0);
      }, { signal:this.signal });
      this.track.addEventListener('keydown', event => {
        if (event.target !== this.track) return;
        if (event.key === 'ArrowRight' || event.key === 'ArrowLeft') {
          event.preventDefault(); this.move(event.key === 'ArrowRight' ? 1 : -1);
        }
      }, { signal:this.signal });
      const visibilityChanged = () => {
        if (document.hidden) this.cancelMove();
        this.schedule();
      };
      document.addEventListener('visibilitychange', visibilityChanged, { signal:this.signal });
      if (this.media.addEventListener) this.media.addEventListener('change', () => {
        this.cancelMove(); this.updateControls(); this.schedule();
      }, { signal:this.signal });
      if (this.root) this.root.addEventListener('ue:motion:change', () => {
        this.updateControls(); this.schedule();
      }, { signal:this.signal });
      if ('IntersectionObserver' in window) {
        this.visibilityObserver = new IntersectionObserver(entries => {
          this.visible = entries.some(entry => entry.isIntersecting);
          if (!this.visible) this.cancelMove();
          this.schedule();
        }, { threshold:.05 });
        this.visibilityObserver.observe(this.track);
      }
      const resize = () => {
        cancelAnimationFrame(this.resizeFrame);
        this.resizeFrame = requestAnimationFrame(() => this.layout());
      };
      if ('ResizeObserver' in window) {
        this.resizeObserver = new ResizeObserver(resize);
        this.resizeObserver.observe(this.track);
      } else window.addEventListener('resize', resize, { passive:true, signal:this.signal });
      document.addEventListener('shopify:block:select', event => {
        const id = event.detail && String(event.detail.blockId);
        const card = this.originals.find(node => node.dataset.ueBlockId === id);
        // Draft editor placeholders deliberately aren't part of the public carousel.
        const draft = !card && Array.from(this.track.children).find(node => node.dataset.ueBlockId === id);
        const target = card || draft;
        if (!target) return;
        this.paused = true; this.hold(); this.cancelMove();
        target.scrollIntoView({ behavior:'auto', block:'nearest', inline:'nearest' });
        this.updateControls();
      }, { signal:this.signal });
      this.layout(true);
    }
    coordinate(node) {
      return node.getBoundingClientRect().left - this.track.getBoundingClientRect().left + this.track.scrollLeft - this.track.clientLeft;
    }
    logicalIndex() {
      return this.step > 0 && this.originals.length ? mod(Math.round((this.track.scrollLeft - this.base) / this.step), this.originals.length) : 0;
    }
    clone(card, index) {
      const node = card.cloneNode(true);
      node.dataset.ueClone = String(index);
      node.removeAttribute('data-ue-card');
      node.removeAttribute('data-ue-block-id');
      node.setAttribute('aria-hidden', 'true');
      node.setAttribute('role', 'presentation');
      [node, ...node.querySelectorAll('*')].forEach(el => {
        el.removeAttribute('id');
        el.removeAttribute('data-shopify-editor-block');
        el.removeAttribute('data-shopify-editor-section');
        el.removeAttribute('aria-labelledby');
        el.removeAttribute('aria-describedby');
        if (el.matches('a,button,input,select,textarea,[tabindex]')) el.setAttribute('tabindex', '-1');
      });
      return node;
    }
    layout(force = false) {
      if (!this.owner.isConnected) return;
      const width = this.track.clientWidth;
      const first = this.originals[0];
      if (!first || width < 2) { this.updateControls(); return; }
      const cardWidth = first.getBoundingClientRect().width;
      const gap = parseFloat(getComputedStyle(this.track).columnGap) || 16;
      if (!force && Math.abs((this.lastWidth || 0) - width) < 1 && Math.abs((this.lastCard || 0) - cardWidth) < 1) return;
      const index = this.logicalIndex();
      this.cancelMove(); this.hold();
      this.track.querySelectorAll('[data-ue-clone]').forEach(node => node.remove());
      this.track.removeAttribute('data-uem-loop-ready');
      this.lastWidth = width; this.lastCard = cardWidth;
      this.step = cardWidth + gap;
      this.cycle = this.step * this.originals.length;
      this.overflow = this.originals.length > 1 && this.cycle - gap > width + 6;
      this.infinite = this.enabled && this.overflow && !this.designMode;
      this.base = 0;
      if (this.infinite) {
        const before = document.createDocumentFragment();
        const after = document.createDocumentFragment();
        this.originals.forEach((card, i) => { before.appendChild(this.clone(card, i)); after.appendChild(this.clone(card, i)); });
        this.track.insertBefore(before, first);
        this.track.appendChild(after);
        this.track.dataset.uemLoopReady = 'true';
        this.base = this.coordinate(first);
      }
      this.track.scrollLeft = this.infinite ? this.base + index * this.step : Math.min(index * this.step, Math.max(0, this.track.scrollWidth - width));
      this.updateControls(); this.schedule();
    }
    normalize() {
      if (!this.infinite || !this.cycle || this.dragging || this.moving || (this.focused && document.activeElement !== this.track)) return;
      const x = this.track.scrollLeft;
      if (x < this.base - 1 || x >= this.base + this.cycle - 1) {
        this.track.scrollLeft = Math.abs(x - this.base - this.cycle) < 1 ? this.base : this.base + mod(x - this.base, this.cycle);
      }
    }
    updateControls() {
      const max = Math.max(0, this.track.scrollWidth - this.track.clientWidth);
      if (this.controls) this.controls.hidden = max < 8;
      if (this.owner.prev) this.owner.prev.disabled = !this.infinite && this.track.scrollLeft <= 3;
      if (this.owner.next) this.owner.next.disabled = !this.infinite && this.track.scrollLeft >= max - 3;
      if (this.autoRow) this.autoRow.hidden = !this.overflow;
      if (this.autoOption) {
        this.autoOption.disabled = !this.enabled || !this.overflow || this.media.matches;
        this.autoOption.checked = this.enabled && this.overflow && !this.paused && !this.media.matches;
        this.autoOption.setAttribute('aria-controls', this.track.id);
      }
      this.track.setAttribute('aria-live', 'off');
    }
    eligible() {
      return this.enabled && this.overflow && !this.paused && !this.hover && !this.focused && !this.dragging && !this.moving && !this.media.matches && !document.hidden && this.visible && this.owner.isConnected;
    }
    hold() { clearTimeout(this.timer); this.timer = null; }
    schedule() {
      this.hold();
      if (this.eligible()) this.timer = setTimeout(() => { if (this.eligible()) this.move(1, true); }, this.delay);
    }
    cancelMove() {
      cancelAnimationFrame(this.animationFrame);
      this.moving = false;
      this.track.removeAttribute('data-uem-moving');
    }
    move(direction, automatic = false) {
      if (!this.originals.length || !this.step) return;
      this.hold(); this.cancelMove();
      this.normalize();
      const start = this.track.scrollLeft;
      const count = this.originals.length;
      let target;
      if (this.infinite) {
        const index = Math.round((start - this.base) / this.step);
        target = this.base + (index + direction) * this.step;
      } else {
        const max = Math.max(0, this.track.scrollWidth - this.track.clientWidth);
        target = Math.max(0, Math.min(max, start + this.step * direction));
        if (automatic && target >= max - 2 && start >= max - 2) target = 0;
      }
      const immediate = this.media.matches;
      if (Math.abs(target - start) < 1 || immediate) {
        this.track.scrollLeft = target;
        this.normalize(); this.updateControls(); this.schedule(); return;
      }
      this.moving = true;
      this.track.dataset.uemMoving = 'true';
      const duration = 620;
      let begin = null;
      const animate = time => {
        if (!this.owner.isConnected || document.hidden) { this.cancelMove(); return; }
        if (begin === null) begin = time;
        const t = Math.min((time - begin) / duration, 1);
        const eased = t < .5 ? 4*t*t*t : 1 - Math.pow(-2*t + 2, 3)/2;
        this.track.scrollLeft = start + (target - start) * eased;
        if (t < 1) this.animationFrame = requestAnimationFrame(animate);
        else {
          this.moving = false;
          this.normalize();
          this.track.removeAttribute('data-uem-moving');
          this.updateControls(); this.schedule();
        }
      };
      this.animationFrame = requestAnimationFrame(animate);
    }
    destroy() {
      this.hold(); this.cancelMove(); this.abort.abort();
      clearTimeout(this.scrollTimer); clearTimeout(this.focusTimer);
      cancelAnimationFrame(this.resizeFrame);
      if (this.visibilityObserver) this.visibilityObserver.disconnect();
      if (this.resizeObserver) this.resizeObserver.disconnect();
      this.track.querySelectorAll('[data-ue-clone]').forEach(node => node.remove());
      this.track.removeAttribute('data-uem-loop-ready');
      if (this.autoRow) this.autoRow.hidden = true;
    }
  }

  class UEProductTrust extends HTMLElement {
    connectedCallback() { this._frame = requestAnimationFrame(() => this.start()); }
    disconnectedCallback() {
      cancelAnimationFrame(this._frame); cancelAnimationFrame(this._syncFrame);
      clearTimeout(this._unlockTimer);
      if (this._abort) this._abort.abort();
      if (this._observer) this._observer.disconnect();
      if (this.slider) { this.slider.destroy(); this.slider = null; }
      this._started = false;
    }
    start() {
      if (this._started || !this.isConnected) return;
      this._started = true;
      this._abort = new AbortController();
      const signal = this._abort.signal;
      this.root = document.getElementById(this.dataset.productRoot || '');
      this.track = this.querySelector('[data-ue-track]');
      this.prev = this.querySelector('[data-ue-prev]');
      this.next = this.querySelector('[data-ue-next]');
      this.status = this.querySelector('[data-ue-status]');
      this.scheduleSync = () => {
        cancelAnimationFrame(this._syncFrame);
        this._syncFrame = requestAnimationFrame(() => this.sync());
      };
      this.addEventListener('click', event => {
        const target = event.target instanceof Element ? event.target : null;
        if (!target) return;
        if (target.closest('[data-ue-add]')) this.addSelected();
        if (target.closest('[data-ue-vin]')) this.verifyVIN();
        if (target.closest('[data-ue-prev]') && this.slider) this.slider.move(-1);
        if (target.closest('[data-ue-next]') && this.slider) this.slider.move(1);
      }, { signal });
      if (this.root) {
        this.root.addEventListener('click', this.scheduleSync, { signal });
        this.root.addEventListener('change', this.scheduleSync, { signal });
        this._observer = new MutationObserver(this.scheduleSync);
        ['[id^="TeMainPrice-"]','[id^="TeAddToCart-"]','[id^="TeVariantId-"]','[id^="TeVariantOptions-"]'].forEach(selector => {
          const node = this.root.querySelector(selector);
          if (node) this._observer.observe(node, { attributes:true, childList:true, subtree:true, characterData:true, attributeFilter:['disabled','aria-disabled','aria-busy','value','class','aria-pressed'] });
        });
      }
      if (this.track) this.slider = new DeliveryLoop(this);
      this.sync();
    }
    native() {
      const button = this.root && this.root.querySelector('[id^="TeAddToCart-"]');
      const form = button && button.form;
      const id = form && form.querySelector('[name="id"]');
      const valid = Boolean(button && form && id && id.value);
      const busy = button && (button.disabled || button.getAttribute('aria-disabled') === 'true' || button.getAttribute('aria-busy') === 'true');
      return { button, form, id, valid, busy };
    }
    sync() {
      const native = this.native();
      const lock = this.root && (addLocks.get(this.root) || 0) > Date.now();
      this.querySelectorAll('[data-ue-add]').forEach(button => {
        button.hidden = !native.valid;
        button.disabled = !native.valid || Boolean(native.busy) || Boolean(lock);
        const nativeText = native.button && native.button.textContent.trim();
        const label = lock ? 'Please wait...' : native.busy ? (nativeText || 'Review selected unit') : 'Add Selected Unit to Cart';
        if (button.textContent !== label) button.textContent = label;
      });
      const vin = this.root && this.root.querySelector('.te-vin-button[data-vin-trigger]');
      this.querySelectorAll('[data-ue-vin]').forEach(button => { button.hidden = !vin; });
      const price = this.root && this.root.querySelector('[id^="TeMainPrice-"]');
      if (price) this.querySelectorAll('[data-ue-price]').forEach(node => {
        if (node.textContent !== price.textContent.trim()) node.textContent = price.textContent.trim();
      });
      if (this.slider) this.slider.updateControls();
    }
    syncRelated() {
      document.querySelectorAll('ue-product-trust').forEach(panel => {
        if (panel.root === this.root && typeof panel.sync === 'function') panel.sync();
      });
    }
    addSelected() {
      const native = this.native();
      if (!native.valid || native.busy || (addLocks.get(this.root) || 0) > Date.now()) {
        if (this.status) this.status.textContent = 'Please review the selected unit and availability above.';
        if (native.button) native.button.scrollIntoView({ behavior:'auto', block:'center' });
        return;
      }
      if (this.status) this.status.textContent = '';
      addLocks.set(this.root, Date.now() + 1200);
      this.syncRelated();
      native.button.click();
      this._unlockTimer = setTimeout(() => {
        if (!this.root) return;
        addLocks.delete(this.root); this.syncRelated();
      }, 1250);
    }
    verifyVIN() {
      const trigger = this.root && this.root.querySelector('.te-vin-button[data-vin-trigger]');
      if (trigger) trigger.click();
      else if (this.status) this.status.textContent = 'Please use the existing fitment help button above, or call our parts team.';
    }
  }
  customElements.define('ue-product-trust', UEProductTrust);
})();